/**
 * charm-picker — multi-select charm grid with per-charm quantity, rendered
 * inside the input modal overlay. Charms are separate purchasable products;
 * tiles re-match to the selected main-product color (option1 index) the same
 * way product-upsell does, and selections are added to the cart as their own
 * line items via CartAdd.getUpsellItems / the input-modal submit fallback.
 */
if (!customElements.get('charm-picker')) {
  customElements.define(
    'charm-picker',
    class CharmPicker extends HTMLElement {
      connectedCallback() {
        // input-modal moves its overlay (and this element) to document.body,
        // so connectedCallback fires again — bind listeners only once.
        if (!this._bound) {
          this._bound = true
          this.grid = this.querySelector('[data-charm-grid]')
          this.propertyInput = this.querySelector('[data-charm-property]')
          this.maxTotal = parseInt(this.dataset.maxTotal || '0', 10) || 0
          this._active = true

          this.querySelectorAll('[data-charm-item]').forEach((item) => {
            item.addEventListener('click', (e) => {
              if (e.target.closest('[data-charm-qty-minus]')) {
                this.changeQty(item, -1)
              } else if (e.target.closest('[data-charm-qty-plus]')) {
                this.changeQty(item, 1)
              } else if (e.target.closest('[data-charm-qty-wrap]')) {
                // clicks on the qty bar itself shouldn't toggle the selection
              } else {
                this.toggleItem(item)
              }
            })
            item.addEventListener('keydown', (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                this.toggleItem(item)
              }
            })
          })

          this.matchInitialVariant()
        }

        if (!this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber = subscribe(
            PUB_SUB_EVENTS.variantChange,
            (event) => {
              if (event.data.variant) this.matchProductVariants(event.data.variant)
            }
          )
        }
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber()
          this.variantChangeUnsubscriber = null
        }
      }

      /* ========== Selection & quantity ========== */

      getQty(item) {
        return parseInt(item.dataset.qty || '0', 10) || 0
      }

      totalQty() {
        let total = 0
        this.querySelectorAll('[data-charm-item]').forEach((item) => {
          total += this.getQty(item)
        })
        return total
      }

      toggleItem(item) {
        if (item.classList.contains('charm-picker--unavailable')) return
        if (this.getQty(item) > 0) {
          this.setQty(item, 0)
        } else {
          if (this.maxTotal > 0 && this.totalQty() >= this.maxTotal) return
          this.setQty(item, 1)
        }
      }

      changeQty(item, delta) {
        if (item.classList.contains('charm-picker--unavailable')) return
        if (delta > 0 && this.maxTotal > 0 && this.totalQty() >= this.maxTotal) return
        this.setQty(item, Math.max(0, this.getQty(item) + delta))
      }

      setQty(item, qty) {
        item.dataset.qty = String(qty)
        item.classList.toggle('is-selected', qty > 0)
        item.setAttribute('aria-pressed', qty > 0 ? 'true' : 'false')
        const qtyWrap = item.querySelector('[data-charm-qty-wrap]')
        const qtyValue = item.querySelector('[data-charm-qty-value]')
        if (qtyWrap) qtyWrap.hidden = qty === 0
        if (qtyValue) qtyValue.textContent = String(qty)
        this.onSelectionChanged()
      }

      onSelectionChanged() {
        this.clearError()
        this.updateMaxState()
        this.updatePropertyInput()
        this.updateSummary()
      }

      updateMaxState() {
        if (this.maxTotal <= 0) return
        const maxed = this.totalQty() >= this.maxTotal
        this.classList.toggle('charm-picker--maxed', maxed)
        this.querySelectorAll('[data-charm-qty-plus]').forEach((btn) => {
          btn.disabled = maxed
        })
      }

      /* ========== Selected items ========== */

      /** [{ item, matchKey, title, qty, variantId, price, image }] for qty > 0 tiles. */
      getSelections() {
        const selections = []
        this.querySelectorAll('[data-charm-item]').forEach((item) => {
          const qty = this.getQty(item)
          if (qty <= 0) return
          selections.push({
            item,
            matchKey: item.dataset.matchKey || '',
            title: item.dataset.title || '',
            qty,
            variantId: parseInt(item.dataset.variantId || '', 10) || 0,
            price: parseInt(item.dataset.price || '0', 10) || 0,
            image: item.dataset.image || '',
          })
        })
        return selections
      }

      /** [{ id, quantity }] for the cart. Empty when picker is inactive (toggle = No). */
      getItems() {
        if (!this._active) return []
        return this.getSelections()
          .filter((s) => s.variantId)
          .map((s) => ({ id: s.variantId, quantity: s.qty }))
      }

      totalPrice() {
        return this.getSelections().reduce((sum, s) => sum + s.price * s.qty, 0)
      }

      /* ========== Main-item property & on-page summary ========== */

      summaryText() {
        return this.getSelections()
          .map((s) => (s.qty > 1 ? `${s.title} ×${s.qty}` : s.title))
          .join(', ')
      }

      updatePropertyInput() {
        if (!this.propertyInput) return
        const text = this._active ? this.summaryText() : ''
        this.propertyInput.value = text
        this.propertyInput.disabled = text === ''
      }

      updateSummary() {
        const summaryId = this.dataset.summaryId
        if (!summaryId) return
        const summary = document.getElementById(summaryId)
        if (!summary) return
        const selections = this._active ? this.getSelections() : []
        if (selections.length === 0) {
          summary.hidden = true
          summary.innerHTML = ''
          return
        }
        const thumbs = selections
          .map((s) => {
            const img = s.image
              ? `<img src="${s.image}" alt="${this.escapeHtml(s.title)}" width="36" height="36" loading="lazy">`
              : ''
            const badge = s.qty > 1 ? `<span class="charm-summary__qty">${s.qty}</span>` : ''
            return `<span class="charm-summary__thumb" title="${this.escapeHtml(s.title)}">${img}${badge}</span>`
          })
          .join('')
        summary.innerHTML = `
          <span class="charm-summary__thumbs">${thumbs}</span>
          <span class="charm-summary__total">+ ${this.formatMoney(this.totalPrice())}</span>
        `
        summary.hidden = false
      }

      escapeHtml(s) {
        const div = document.createElement('div')
        div.textContent = s == null ? '' : String(s)
        return div.innerHTML
      }

      formatMoney(cents) {
        return Shopify.formatMoney ? Shopify.formatMoney(cents) : (cents / 100).toFixed(2)
      }

      /* ========== Variant (color) matching — same pattern as product-upsell ========== */

      matchInitialVariant() {
        const option1Index = parseInt(this.getAttribute('data-initial-option1-index') || '', 10)
        const option1 = this.getAttribute('data-initial-option1')
        if (!Number.isNaN(option1Index) && option1Index >= 0) {
          this.matchProductVariants({ option1Index })
        } else if (option1) {
          this.matchProductVariants({ option1 })
        }
      }

      normalizeOption(s) {
        if (s == null) return ''
        const el = document.createElement('textarea')
        el.innerHTML = String(s)
        return el.value.replace(/\s+/g, ' ').trim().toLowerCase()
      }

      getOption1Index(variant) {
        if (variant != null && typeof variant.option1Index === 'number' && variant.option1Index >= 0) {
          return variant.option1Index
        }
        const raw = this.getAttribute('data-product-options')
        if (!raw || !variant || variant.option1 == null) return -1
        try {
          const options = JSON.parse(raw)
          const opt1 = options.find((o) => o.position === 1)
          if (opt1 && opt1.values) return opt1.values.indexOf(variant.option1)
        } catch (e) {
          /* ignore */
        }
        return -1
      }

      matchProductVariants(variant) {
        const option1Index = this.getOption1Index(variant)
        const option1 = variant ? this.normalizeOption(variant.option1) : ''

        this.querySelectorAll('[data-charm-item][data-product-variants]').forEach((item) => {
          try {
            const rawJson = item.getAttribute('data-product-variants')
            if (!rawJson) return
            const variants = JSON.parse(rawJson)
            const match =
              (option1Index >= 0 &&
                (variants.find((v) => v.o1i === option1Index && v.a) ||
                  variants.find((v) => v.o1i === option1Index))) ||
              variants.find((v) => this.normalizeOption(v.o1) === option1 && v.a) ||
              variants.find((v) => this.normalizeOption(v.o1) === option1)
            if (match) {
              item.dataset.variantId = String(match.id)
              item.classList.remove('charm-picker--unavailable')
              if (match.p !== undefined) {
                item.dataset.price = String(match.p)
                const priceEl = item.querySelector('[data-charm-item-price]')
                if (priceEl) priceEl.textContent = `+ ${this.formatMoney(match.p)}`
              }
              if (match.i && !item.dataset.imageFromOption) {
                item.dataset.image = match.i
                item.querySelectorAll('.charm-picker__item-image img').forEach((img) => {
                  img.src = match.i
                })
              }
            } else {
              item.dataset.variantId = ''
              item.classList.add('charm-picker--unavailable')
              if (this.getQty(item) > 0) this.setQty(item, 0)
            }
          } catch (e) {
            /* ignore parse errors */
          }
        })
        this.onSelectionChanged()
      }

      /* ========== Input-modal integration ========== */

      /** Toggle "Yes"/"No" state from the host input-modal: keeps selections but withholds items/property/summary. */
      setActive(active) {
        this._active = active !== false
        this.updatePropertyInput()
        this.updateSummary()
      }

      validate() {
        const field = this.closest('[data-input-modal-field]')
        const required = field?.dataset?.inputModalFieldRequired === 'true'
        if (!required) return true
        if (this.getItems().length > 0) return true
        this.showError(this.dataset.errorMessage || 'This field is required.')
        return false
      }

      showError(message) {
        const field = this.closest('[data-input-modal-field]')
        if (!field) return
        field.classList.add('input-modal__field--error')
        const err = field.querySelector('[data-input-modal-field-error-text]')
        if (err) {
          err.textContent = message
          err.hidden = false
        }
      }

      clearError() {
        const field = this.closest('[data-input-modal-field]')
        if (!field) return
        field.classList.remove('input-modal__field--error')
        const err = field.querySelector('[data-input-modal-field-error-text]')
        if (err) {
          err.textContent = ''
          err.hidden = true
        }
      }

      reset() {
        this.querySelectorAll('[data-charm-item]').forEach((item) => {
          if (this.getQty(item) > 0) this.setQty(item, 0)
        })
        this._active = true
        this.clearError()
        this.updatePropertyInput()
        this.updateSummary()
      }
    }
  )
}
